import { displayDateUS } from "../../../../utils/dateUtils";

const escapeHtml = (value) => {
  if (value == null) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

const PRINT_STYLES = `
  @media print {
    @page { margin: 0.45in; size: letter portrait; }
    body { margin: 0; padding: 0; }
  }
  * { box-sizing: border-box; }
  .dl-print {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 10pt;
    line-height: 1.35;
    color: #0f172a;
    max-width: 780px;
    margin: 0 auto;
  }
  .dl-print .title {
    text-align: center;
    padding-bottom: 8px;
  }
  .dl-print .title h1 {
    margin: 0;
    font-size: 13pt;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: #1e293b;
  }
  .dl-print .title p {
    margin: 4px 0 0;
    font-size: 9pt;
    color: #64748b;
  }
  .dl-print .title .rule {
    display: block;
    width: 56px;
    height: 3px;
    margin: 8px auto 0;
    background: #a5b4fc;
    border-radius: 999px;
  }
  .dl-print .identity {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
    margin-bottom: 12px;
    background: #f8fafc;
  }
  .dl-print .identity-grid {
    display: grid;
    grid-template-columns: 1.2fr 0.9fr 0.9fr 1.2fr 0.9fr 0.75fr 0.75fr;
    gap: 8px 12px;
    padding: 8px 12px;
  }
  .dl-print .field-label {
    display: block;
    font-size: 7pt;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #94a3b8;
  }
  .dl-print .field-value {
    font-size: 10pt;
    font-weight: 700;
    color: #0f172a;
    min-height: 14px;
  }
  .dl-print .meta {
    font-size: 8.5pt;
    color: #64748b;
    margin: 0 0 12px;
  }
  .dl-print .section {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
    margin-bottom: 12px;
  }
  .dl-print .section-head {
    padding: 6px 10px;
    background: #eef2ff;
    border-bottom: 1px solid #e0e7ff;
    font-size: 8.5pt;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #3730a3;
  }
  .dl-print .section-body { padding: 10px; }
  .dl-print .notes {
    min-height: 72px;
    white-space: pre-wrap;
    font-size: 10pt;
  }
  .dl-print .sig-box {
    border: 1px dashed #cbd5e1;
    border-radius: 6px;
    background: #f8fafc;
    min-height: 72px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
  }
  .dl-print .sig-box img {
    max-height: 64px;
    max-width: 100%;
    object-fit: contain;
  }
  .dl-print .sig-empty {
    font-size: 8pt;
    font-style: italic;
    color: #94a3b8;
  }
  .dl-print .ack {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 12px;
  }
  .dl-print .signed-by {
    border-bottom: 1px solid #94a3b8;
    min-height: 22px;
    font-size: 10pt;
    font-weight: 700;
  }
`;

const resolveSignatureSrc = (data) => {
  const candidates = [
    data?.patientSignatureUrl,
    data?.patientSignatureImage,
    data?.patientSignatureS3Url,
  ];
  for (const raw of candidates) {
    if (!raw || typeof raw !== "string") continue;
    const v = raw.trim();
    if (
      v.startsWith("data:image") ||
      v.startsWith("http://") ||
      v.startsWith("https://") ||
      v.startsWith("/")
    ) {
      return v;
    }
  }
  return "";
};

const cell = (label, value) =>
  `<div><span class="field-label">${label}</span><div class="field-value">${
    value || "—"
  }</div></div>`;

/**
 * @param {Record<string, unknown>} dailyLog
 * @param {{ logoUrl?: string, companyName?: string }} [branding]
 * @returns {{ html: string, title: string }}
 */
export function buildDailyLogPrintDocument(dailyLog, branding = {}) {
  const data = dailyLog || {};
  const companyName = branding.companyName || "";
  const employeeName = escapeHtml(data.employeeName || data.applicantName || "");
  const role = escapeHtml(data.title || data.filledByRole || "");
  const caregiver = role ? `${employeeName} (${role})` : employeeName;
  const patientName = escapeHtml(data.patientName || "");
  const mrn = escapeHtml(data.patientMrn || data.mrn || "");
  const dob = escapeHtml(displayDateUS(data.patientDateOfBirth) || "");
  const serviceDate = escapeHtml(displayDateUS(data.serviceDate) || "");
  const timeIn = escapeHtml(data.timeIn || "");
  const timeOut = escapeHtml(data.timeOut || "");
  const notes = escapeHtml(data.dailyNotes || "");
  const signedAt = escapeHtml(
    displayDateUS(data.applicantSignedAt) || displayDateUS(data.createdAt) || "",
  );
  const sigSrc = resolveSignatureSrc(data);
  const sigHtml = sigSrc
    ? `<img src="${escapeHtml(sigSrc)}" alt="Patient signature" />`
    : `<span class="sig-empty">Not signed</span>`;
  const employeeSig = escapeHtml(data.applicantSignature || "");
  const reviewerName = escapeHtml(
    data.reviewedBySignedBy ||
      (data.reviewedBy
        ? `${data.reviewedBy.firstName || ""} ${data.reviewedBy.lastName || ""}`.trim()
        : ""),
  );
  const reviewerSig = escapeHtml(data.reviewedBySignature || "");
  const reviewerSignedAt = escapeHtml(
    displayDateUS(data.reviewedBySignedAt) || "",
  );

  const body = `
    <div class="dl-print">
      <div class="title">
        <h1>Daily Log</h1>
        <p>Daily visit documentation for home health services.${
          companyName ? ` ${escapeHtml(companyName)}.` : ""
        }</p>
        <span class="rule"></span>
      </div>

      <div class="identity">
        <div class="identity-grid">
          ${cell("Patient Name", patientName)}
          ${cell("MR Number", mrn)}
          ${cell("Date of Birth", dob)}
          ${cell("Caregiver Name", caregiver)}
          ${cell("Service Date", serviceDate)}
          ${cell("Time In", timeIn)}
          ${cell("Time Out", timeOut)}
        </div>
      </div>
      <p class="meta">Filled by ${caregiver || "—"} · Schedule Date ${serviceDate || "—"}</p>

      <div class="section">
        <div class="section-head">1. Daily Notes</div>
        <div class="section-body"><div class="notes">${notes || "—"}</div></div>
      </div>

      <div class="section">
        <div class="section-head">Patient / Caregiver (Acknowledgment of Visit)</div>
        <div class="section-body">
          <div class="ack">
            <div>
              <span class="field-label">Patient Electronic Signature</span>
              <div class="sig-box">${sigHtml}</div>
            </div>
            <div>
              ${cell("Patient / Representative Name", patientName)}
              ${cell("Signed Date", serviceDate)}
              ${cell("Time Stamp", timeIn || "—")}
            </div>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-head">Employee Authentication</div>
        <div class="section-body">
          ${cell("Signing Employee", employeeName)}
          <p class="field-label" style="margin-top:10px">This form has been electronically signed by:</p>
          <div class="signed-by">${employeeSig || "—"}</div>
        </div>
      </div>

      ${
        reviewerSig || reviewerName || reviewerSignedAt
          ? `<div class="section">
        <div class="section-head">Office / Admin Authentication</div>
        <div class="section-body">
          ${cell("Signing Admin", reviewerName)}
          ${cell("Date Signed", reviewerSignedAt)}
          <p class="field-label" style="margin-top:10px">This form has been electronically signed by:</p>
          <div class="signed-by">${reviewerSig || "—"}</div>
        </div>
      </div>`
          : ""
      }
    </div>
  `;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Daily Log - ${
    patientName || employeeName || "Form"
  }</title><style>${PRINT_STYLES}</style></head><body>${body}</body></html>`;
  return {
    html,
    title: `Daily Log — ${data.patientName || data.employeeName || "Form"}`,
  };
}
